import type { PriceChange } from '../types/schema';
import type { Exchange, NormalizedMarketItem, NormalizedTicker } from '../types/exchange';

// --- 공통 ---

interface MarketRaw {
  market: string;
  korean_name: string;
  english_name: string;
}

const normalizeMarkets = (data: MarketRaw[], exchange: Exchange): NormalizedMarketItem[] =>
  data.map(item => {
    const [quote, symbol] = item.market.split('-');
    return { exchange, market: item.market, korean_name: item.korean_name, english_name: item.english_name, symbol, quote };
  });

/** REST 응답에서 price/openPrice(string)를 파싱하여 NormalizedTicker 생성 (레거시) */
const normalizeRestTicker = (
  exchange: Exchange,
  market: string,
  priceStr: string,
  openPriceStr: string,
  volumeStr: string,
): NormalizedTicker => {
  const price = parseFloat(priceStr);
  const openPrice = parseFloat(openPriceStr);
  const priceChange = price - openPrice;
  const changeRate = openPrice !== 0 ? (priceChange / openPrice) * 100 : 0;
  const change: PriceChange = priceChange > 0 ? 'RISE' : priceChange < 0 ? 'FALL' : 'EVEN';
  const volume = parseFloat(volumeStr);

  return {
    exchange,
    market,
    trade_price: price,
    prev_closing_price: openPrice,
    change,
    change_price: Math.abs(priceChange),
    change_rate: changeRate,
    signed_change_price: priceChange,
    signed_change_rate: changeRate,
    acc_trade_volume_24h: volume,
    acc_trade_price_24h: price * volume,
  };
};

// --- 백엔드 UnifiedTickerRes (camelCase) ---

interface UnifiedTickerRes {
  market: string;
  tradePrice: number;
  openingPrice: number;
  highPrice: number;
  lowPrice: number;
  change: PriceChange;
  signedChangeRate: number;
  signedChangePrice: number;
  accTradeVolume24h: number;
  accTradePrice24h: number | null;
  timestamp: number;
}

/** 백엔드 통합 응답인지 감지 (camelCase `tradePrice` 필드 존재 여부) */
const isUnifiedTickerRes = (raw: unknown): raw is UnifiedTickerRes =>
  typeof raw === 'object' && raw !== null && 'tradePrice' in raw;

/** 백엔드 UnifiedTickerRes (camelCase) → NormalizedTicker (snake_case) */
const normalizeUnifiedTicker = (exchange: Exchange, raw: UnifiedTickerRes): NormalizedTicker => {
  const tradePrice = raw.tradePrice ?? 0;
  const openingPrice = raw.openingPrice ?? 0;
  const changePrice = Math.abs(tradePrice - openingPrice);
  const changeRate = openingPrice !== 0 ? ((tradePrice - openingPrice) / openingPrice) : 0;

  return {
    exchange,
    market: raw.market,
    trade_price: tradePrice,
    prev_closing_price: openingPrice,
    opening_price: openingPrice,
    high_price: raw.highPrice,
    low_price: raw.lowPrice,
    change: raw.change,
    change_price: changePrice,
    change_rate: changeRate,
    signed_change_price: raw.signedChangePrice ?? (tradePrice - openingPrice),
    signed_change_rate: raw.signedChangeRate ?? changeRate,
    // 🔴 TRAD-2382 — 여기 `?? 0` 이 남아 있었다. 바로 아래 거래대금은 TRAD-2370 에서
    //    "없으면 없다고 말한다"로 고쳤는데 **거래량만 부재를 `0` 으로 위장**하고 있었다.
    //    `0`("24시간 동안 거래가 없었다")과 부재("서버가 값을 안 줬다")는 다른 사실이다.
    acc_trade_volume_24h: raw.accTradeVolume24h ?? null,
    // 🔴 TRAD-2370 — 폴백을 두지 않는다. 이전엔 `?? (tradePrice * accTradeVolume24h)` 로
    //    **클라가 곱해 만든 추정치**를 서버 값인 것처럼 내놨고, 볼륨까지 없으면 그 곱이 `0` 이
    //    되어 화면이 "거래대금 0"이라는 **사실 주장**을 하게 됐다. 없으면 없다고 말한다.
    acc_trade_price_24h: raw.accTradePrice24h ?? null,
  };
};

// --- Upbit ---

interface UpbitTickerRaw {
  code?: string;
  market?: string;
  trade_price: number;
  prev_closing_price: number;
  change: PriceChange;
  change_price: number;
  change_rate: number;
  signed_change_price: number;
  signed_change_rate: number;
  acc_trade_volume_24h: number;
  acc_trade_price_24h: number;
}

export const normalizeUpbitMarkets = (data: MarketRaw[]): NormalizedMarketItem[] =>
  normalizeMarkets(data, 'UPBIT');

export const normalizeUpbitTicker = (raw: UpbitTickerRaw | unknown): NormalizedTicker => {
  // 백엔드 통합 응답 (camelCase) 감지
  if (isUnifiedTickerRes(raw)) return normalizeUnifiedTicker('UPBIT', raw);

  // WebSocket 응답 (snake_case)
  const ws = raw as UpbitTickerRaw;
  const market = ws.code ?? ws.market ?? '';
  const changeRate = ws.prev_closing_price
    ? ((ws.trade_price - ws.prev_closing_price) * 100) / ws.prev_closing_price
    : 0;

  return {
    exchange: 'UPBIT',
    market,
    trade_price: ws.trade_price,
    prev_closing_price: ws.prev_closing_price,
    change: ws.change,
    change_price: ws.change_price,
    change_rate: changeRate,
    signed_change_price: ws.signed_change_price,
    signed_change_rate: ws.signed_change_rate,
    acc_trade_volume_24h: ws.acc_trade_volume_24h,
    acc_trade_price_24h: ws.acc_trade_price_24h,
  };
};

// --- Bithumb ---

/** REST API 응답: {symbol: "XRP_KRW", price: "2016", openPrice: "2041", ...} */
interface BithumbTickerRestRaw {
  symbol: string;
  price: string;
  openPrice: string;
  highPrice: string;
  lowPrice: string;
  volume: string;
  eventTime: number;
}

/** WebSocket 응답 (Upbit와 동일 포맷) */
interface BithumbTickerWsRaw {
  code?: string;
  market?: string;
  trade_price: number;
  prev_closing_price: number;
  opening_price?: number;
  high_price?: number;
  low_price?: number;
  change: string;
  change_price: number;
  change_rate: number;
  signed_change_price: number;
  signed_change_rate: number;
  acc_trade_volume_24h: number;
  acc_trade_price_24h: number;
  highest_52_week_price?: number;
  highest_52_week_date?: string;
  lowest_52_week_price?: number;
  lowest_52_week_date?: string;
}

export const normalizeBithumbMarkets = (data: MarketRaw[]): NormalizedMarketItem[] =>
  normalizeMarkets(data, 'BITHUMB');

/** symbol "XRP_KRW" → market "KRW-XRP" */
const bithumbSymbolToMarket = (symbol: string): string => {
  const parts = symbol.split('_');
  if (parts.length === 2) return `${parts[1]}-${parts[0]}`;
  return symbol;
};

export const normalizeBithumbTicker = (raw: BithumbTickerRestRaw | BithumbTickerWsRaw | unknown): NormalizedTicker => {
  // 백엔드 통합 응답 (camelCase) 감지
  if (isUnifiedTickerRes(raw)) return normalizeUnifiedTicker('BITHUMB', raw);

  // REST 응답 감지: symbol 필드가 있고 price가 string
  if ('symbol' in (raw as BithumbTickerRestRaw) && typeof (raw as BithumbTickerRestRaw).price === 'string') {
    const rest = raw as BithumbTickerRestRaw;
    return normalizeRestTicker('BITHUMB', bithumbSymbolToMarket(rest.symbol), rest.price, rest.openPrice, rest.volume);
  }

  // WebSocket 응답 (Upbit와 동일 포맷)
  const ws = raw as BithumbTickerWsRaw;
  const market = ws.code ?? ws.market ?? '';
  const changeRate = ws.prev_closing_price
    ? ((ws.trade_price - ws.prev_closing_price) * 100) / ws.prev_closing_price
    : 0;
  const change: PriceChange = ws.change === 'RISE' ? 'RISE' : ws.change === 'FALL' ? 'FALL' : 'EVEN';

  return {
    exchange: 'BITHUMB',
    market,
    trade_price: ws.trade_price,
    prev_closing_price: ws.prev_closing_price,
    opening_price: ws.opening_price,
    high_price: ws.high_price,
    low_price: ws.low_price,
    change,
    change_price: ws.change_price,
    change_rate: changeRate,
    signed_change_price: ws.signed_change_price,
    signed_change_rate: ws.signed_change_rate,
    acc_trade_volume_24h: ws.acc_trade_volume_24h,
    acc_trade_price_24h: ws.acc_trade_price_24h,
    highest_52_week_price: ws.highest_52_week_price,
    highest_52_week_date: ws.highest_52_week_date,
    lowest_52_week_price: ws.lowest_52_week_price,
    lowest_52_week_date: ws.lowest_52_week_date,
  };
};

// --- Binance ---

/** WebSocket 응답 */
interface BinanceTickerWsRaw {
  s: string; // symbol (e.g. BTCUSDT)
  c: string; // current price
  o: string; // open price (24h)
  h: string; // high price
  l: string; // low price
  x?: string; // prev close price
  p: string; // price change
  P: string; // price change percent
  v: string; // base asset volume
  q: string; // quote asset volume
}

/** REST API 응답: {symbol: "SUSHIUSDT", price: "0.19760000", openPrice: "0.20210000", ...} */
interface BinanceTickerRestRaw {
  symbol: string;
  price: string;
  openPrice: string;
  highPrice: string;
  lowPrice: string;
  volume: string;
  eventTime: number;
}

export const normalizeBinanceMarkets = (data: MarketRaw[]): NormalizedMarketItem[] =>
  normalizeMarkets(data, 'BINANCE');

/** BTCUSDT → USDT-BTC (known quote assets로 분리) */
const BINANCE_QUOTE_ASSETS = ['USDT', 'BUSD', 'BTC', 'ETH', 'BNB'] as const;

const binanceSymbolToMarket = (symbol: string): string => {
  for (const quote of BINANCE_QUOTE_ASSETS) {
    if (symbol.endsWith(quote)) {
      return `${quote}-${symbol.slice(0, -quote.length)}`;
    }
  }
  return symbol;
};

export const normalizeBinanceTicker = (raw: BinanceTickerRestRaw | BinanceTickerWsRaw | unknown): NormalizedTicker => {
  // 백엔드 통합 응답 (camelCase) 감지
  if (isUnifiedTickerRes(raw)) return normalizeUnifiedTicker('BINANCE', raw);

  // REST 응답 감지: price 필드가 있고 s 필드가 없음
  if ('price' in (raw as BinanceTickerRestRaw) && !('s' in (raw as BinanceTickerWsRaw))) {
    const rest = raw as BinanceTickerRestRaw;
    return normalizeRestTicker('BINANCE', binanceSymbolToMarket(rest.symbol), rest.price, rest.openPrice, rest.volume);
  }

  // WebSocket 응답
  const ws = raw as BinanceTickerWsRaw;
  const price = parseFloat(ws.c);
  const openPrice = parseFloat(ws.o);
  const priceChange = parseFloat(ws.p);
  const priceChangePercent = parseFloat(ws.P);
  const change: PriceChange = priceChange > 0 ? 'RISE' : priceChange < 0 ? 'FALL' : 'EVEN';

  return {
    exchange: 'BINANCE',
    market: binanceSymbolToMarket(ws.s),
    trade_price: price,
    prev_closing_price: ws.x ? parseFloat(ws.x) : openPrice,
    opening_price: openPrice,
    high_price: parseFloat(ws.h),
    low_price: parseFloat(ws.l),
    change,
    change_price: Math.abs(priceChange),
    change_rate: priceChangePercent,
    signed_change_price: priceChange,
    signed_change_rate: priceChangePercent / 100,
    acc_trade_volume_24h: parseFloat(ws.v),
    acc_trade_price_24h: parseFloat(ws.q),
  };
};

// --- Coinone (TRAD-3635) ---
//
// 출처 2가지를 «같은 값»으로 정규화한다:
//  ① REST — 서버 경유 /api/v1/coinone/ticker (UnifiedTickerRes, backend TRAD-3634). openingPrice = WS first.
//  ② WS — 브라우저 직결 wss://stream.coinone.co.kr TICKER(SHORT 포맷 구독 — 키가 짧다, 아래 표로 펼친다).
// ~~🔴 등락 기준 = first(«UTC 0시 = KST 9시» 시가). 업비트·코인원 = UTC 0시(KST 9시) 같은 경계, 빗썸 = 24H(미재확인).~~
//    ~~업비트(KST 0시)~~ — 종전 주석이 틀렸다(업비트 일봉 경계도 KST 09:00, 팀장 정정 2026-10-08). 코드는 원래 first 기준이라 무변경.
// ~~🔴 first(openingPrice)가 없으면 등락을 0 으로 지어내지 않는다 — null 을 돌려 호출부가 그 값을 쓰지 않게 한다.~~
// → 🔴 TRAD-3664(davidyoon 결정 2026-10-09): 등락 기준 = **전일 종가(yesterday_last)** — 2026-10-08 «first 기준» 결정(docs-ops/
//    2026-10-07-coinone-ios-design.md §6-2)을 뒤집었다. 근거·5지점 표 = docs-ops/2026-10-09-TRAD-3664-coinone-change-basis-research.md.
//    전일 종가가 없으면 등락 4필드·change 를 **null(«값 없음»)** 으로 둔다 — 시가로 스스로 만들지 않는다(backend 도 null 로 내린다).
//    REST 는 서버 값만 쓴다(종전 `?? 시가 기준 로컬 계산` 폴백 삭제). backend·iOS·android 와 같은 묶음으로 나간다.

/** SHORT 포맷 키 → 기본 포맷 키(2026-10-07 실측 SHORT 프레임 21키). 이 표가 정규화의 유일한 매핑이다. */
export const COINONE_SHORT_KEYS = {
  qc: 'quote_currency',
  tc: 'target_currency',
  t: 'timestamp',
  qv: 'quote_volume',
  tv: 'target_volume',
  hi: 'high',
  lo: 'low',
  fi: 'first',
  la: 'last',
  vp: 'volume_power',
  abp: 'ask_best_price',
  abq: 'ask_best_qty',
  bbp: 'bid_best_price',
  bbq: 'bid_best_qty',
  i: 'id',
  yhi: 'yesterday_high',
  ylo: 'yesterday_low',
  yfi: 'yesterday_first',
  yla: 'yesterday_last',
  yqv: 'yesterday_quote_volume',
  ytv: 'yesterday_target_volume',
} as const;

/** 기본 포맷 TICKER data(문자열 숫자 — 실측). */
export interface CoinoneTickerData {
  quote_currency: string;
  target_currency: string;
  timestamp?: number;
  quote_volume?: string;
  target_volume?: string;
  high?: string;
  low?: string;
  first?: string;
  last: string;
  yesterday_last?: string;
  [key: string]: unknown;
}

/**
 * WS 프레임 → 기본 포맷 data. DATA·TICKER 가 아니면 null(CONNECTED·SUBSCRIBED·PONG·ERROR 는 시세가 아니다).
 * 기본 `{response_type:'DATA', channel:'TICKER', data}` / SHORT `{r:'DATA', c:'TICKER', d}` 둘 다 받는다.
 */
export const expandCoinoneTickerFrame = (frame: unknown): CoinoneTickerData | null => {
  if (typeof frame !== 'object' || frame === null) return null;
  const f = frame as Record<string, unknown>;
  if (f.response_type === 'DATA' && f.channel === 'TICKER' && typeof f.data === 'object' && f.data) {
    return f.data as CoinoneTickerData;
  }
  if (f.r === 'DATA' && f.c === 'TICKER' && typeof f.d === 'object' && f.d) {
    const short = f.d as Record<string, unknown>;
    return Object.fromEntries(
      Object.entries(short).map(([k, v]) => [COINONE_SHORT_KEYS[k as keyof typeof COINONE_SHORT_KEYS] ?? k, v]),
    ) as CoinoneTickerData;
  }
  return null;
};

const toNum = (v: unknown): number | undefined => {
  if (v === null || v === undefined || v === '') return undefined;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : undefined;
};

/**
 * 등락 계산(WS) — TRAD-3664: 기준 = 전일 종가(yesterday_last). REST 는 서버가 같은 기준으로 계산해 내려준다.
 * 기준이 없거나 0 이면 null 3개 — «0%»(보합)라는 거짓을 만들지 않는다.
 */
const coinoneChange = (
  last: number,
  prevClose: number | undefined,
): { signed: number | null; rate: number | null; change: PriceChange | null } => {
  if (prevClose === undefined || prevClose === 0) return { signed: null, rate: null, change: null };
  const signed = last - prevClose;
  const rate = signed / prevClose;
  const change: PriceChange = signed > 0 ? 'RISE' : signed < 0 ? 'FALL' : 'EVEN';
  return { signed, rate, change };
};

export const normalizeCoinoneMarkets = (data: MarketRaw[]): NormalizedMarketItem[] => normalizeMarkets(data, 'COINONE');

/**
 * WS TICKER data → NormalizedTicker. last 가 없으면 null(시세 자체가 없다).
 * TRAD-3664 — ~~last·first 가 없으면 null~~ → first(시가)는 표시용이라 없어도 행을 만든다. 등락은 yesterday_last 기준, 없으면 null.
 */
export const normalizeCoinoneWsTicker = (d: CoinoneTickerData): NormalizedTicker | null => {
  const last = toNum(d.last);
  if (last === undefined) return null;
  const first = toNum(d.first);
  const prevClose = toNum(d.yesterday_last);
  const { signed, rate, change } = coinoneChange(last, prevClose);
  return {
    exchange: 'COINONE',
    market: `${String(d.quote_currency).toUpperCase()}-${String(d.target_currency).toUpperCase()}`,
    trade_price: last,
    // 전일 종가 = yesterday_last(UTC 전일 마지막 체결가). 없으면 undefined(«—») — 시가를 전일 종가로 쓰지 않는다.
    prev_closing_price: prevClose as number,
    opening_price: first,
    high_price: toNum(d.high),
    low_price: toNum(d.low),
    change,
    change_price: signed === null ? null : Math.abs(signed),
    change_rate: rate,
    signed_change_price: signed,
    signed_change_rate: rate,
    acc_trade_volume_24h: toNum(d.target_volume) ?? null,
    acc_trade_price_24h: toNum(d.quote_volume) ?? null,
  };
};

interface CoinoneRestTicker {
  market: string;
  tradePrice: number;
  openingPrice: number | null;
  highPrice?: number | null;
  lowPrice?: number | null;
  change?: PriceChange | null;
  signedChangeRate?: number | null;
  signedChangePrice?: number | null;
  prevClosingPrice?: number | null;
  accTradeVolume24h?: number | null;
  accTradePrice24h?: number | null;
}

/**
 * REST(서버 경유) 행 → NormalizedTicker. 시세(tradePrice)가 없으면 null.
 * TRAD-3664 — ~~openingPrice 가 없으면 null · 서버 등락값이 없으면 시가 기준 로컬 계산으로 채움~~ → 등락은 **서버 값만**.
 * 서버는 전일 종가가 없으면 change 계열을 null 로 내린다(결정) → 여기서도 null(«값 없음»). 시가로 스스로 만들지 않는다.
 */
export const normalizeCoinoneTicker = (raw: CoinoneRestTicker | unknown): NormalizedTicker | null => {
  const r = raw as CoinoneRestTicker;
  const last = toNum(r?.tradePrice);
  if (!r || last === undefined) return null;
  const first = toNum(r.openingPrice);
  const signed = toNum(r.signedChangePrice) ?? null;
  const rate = toNum(r.signedChangeRate) ?? null;
  return {
    exchange: 'COINONE',
    market: String(r.market).toUpperCase(),
    trade_price: last,
    prev_closing_price: toNum(r.prevClosingPrice) as number,
    opening_price: first,
    high_price: toNum(r.highPrice),
    low_price: toNum(r.lowPrice),
    change: r.change ?? null,
    change_price: signed === null ? null : Math.abs(signed),
    change_rate: rate,
    signed_change_price: signed,
    signed_change_rate: rate,
    acc_trade_volume_24h: toNum(r.accTradeVolume24h) ?? null,
    acc_trade_price_24h: toNum(r.accTradePrice24h) ?? null,
  };
};
